import { createContext, useContext, useState, type ReactNode } from 'react';
import { LinkContext, type LinkComponent } from '../primitives/Link/Link';

/**
 * App-level services the library needs but must not own. Not in Figma.
 *
 * - `linkComponent` — how internal links navigate (the app passes its router
 *   link; the default is a plain `<a>`).
 * - `today` — the clock date pickers and presets treat as "now". The app passes
 *   its data source's clock so a fixed-date dataset (the `/test` mock) and its
 *   presets agree. Defaults to the real date.
 *
 * Everything is optional, so Storybook and tests render without a provider.
 */
export type UIProviderProps = {
  linkComponent?: LinkComponent;
  today?: Date;
  children: ReactNode;
};

const TodayContext = createContext<Date | undefined>(undefined);

export function UIProvider({ linkComponent, today, children }: UIProviderProps) {
  const withToday = <TodayContext.Provider value={today}>{children}</TodayContext.Provider>;
  return linkComponent ? (
    <LinkContext.Provider value={linkComponent}>{withToday}</LinkContext.Provider>
  ) : (
    withToday
  );
}

/** The date the UI should treat as today — `UIProvider`'s `today`, else the real date. */
export function useToday(): Date {
  const provided = useContext(TodayContext);
  // Captured once per component so memo dependencies stay stable across renders.
  const [fallback] = useState(() => new Date());
  return provided ?? fallback;
}
